'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { throttle } from 'lodash-es';
import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';

import { Plus } from '@zoomit/icons';
import { Flex } from '@zoomit/primitives';
import { OriginURL, themeStates, ToggleTheme } from '@zoomit/web-kit';
import { cssVar, theme } from '@zoomit/zoomit-ds';

import { useWebKitCustomization } from '../../../hooks/useWebKitCustomization';
import type { MenuParserData } from '../../../types/menus';
import { DefaultMenuMoreItems } from '../default-menu/DefaultMenuMoreItems';
import { MenuItems } from './MenuItems';
import { SearchSection } from './SearchSection';
import { UserSection } from './UserSection';

export const SearchOrientedMenuDesktop = (props: MenuParserData) => {
  const { searchInstanceRenderer, menuData } = props;

  const [openThemeToggle, setOpenThemeToggle] = useState(false);
  const searchSettings = useWebKitCustomization((settings) => settings.search);

  const { theme: selectedTheme } = useTheme();
  const ThemeIcon = selectedTheme ? themeStates[selectedTheme].icon : themeStates.system.icon;
  const basePath = useWebKitCustomization((settings) => settings.basePath);
  const tenantLogoRenderer = useWebKitCustomization((settings) => settings.tenantLogoRenderer);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    let lastPos = window.scrollY;

    const handleScroll = throttle(() => {
      const curPos = window.scrollY;

      if (!ref.current || curPos < 300) {
        lastPos = curPos;
        return;
      }

      if (curPos > lastPos) {
        ref.current.style.transform = `translateY(calc(-100% + 2px))`;
      } else {
        ref.current.style.transform = `translateY(0px)`;
      }

      lastPos = curPos;
    }, 100);
    document.addEventListener('scroll', handleScroll);

    return () => {
      ref.current && (ref.current.style.transform = `translateY(0px)`);
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      data-testid="menu-desktop"
      className={'pointer-events-none sticky top-0 z-50 hidden items-start justify-between lg:block'}
    >
      <div className="bg-background2 pointer-events-auto relative z-10 flex items-center gap-5 px-8 pt-4 pb-5 md:grid md:grid-cols-3 md:gap-10">
        <a href={`${OriginURL}/${basePath}`}>{tenantLogoRenderer({ fill: cssVar(theme.text) })}</a>

        <SearchSection
          searchInstanceRenderer={searchInstanceRenderer}
          className="w-[648px] justify-self-center"
        />

        <div className="flex flex-nowrap items-center gap-5 justify-self-end">
          <AnimatePresence>
            {ThemeIcon && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                whileTap={{ scale: 0.9 }}
                transition={{ delay: 0.2 }}
                className={'relative z-50 flex cursor-pointer items-center justify-center'}
                onClick={() => setOpenThemeToggle((prev) => !prev)}
              >
                <ThemeIcon size="md" fill={cssVar(theme.text)} />
                <ToggleTheme open={openThemeToggle} setOpen={setOpenThemeToggle} />
              </motion.div>
            )}
          </AnimatePresence>
          <UserSection Logo={() => tenantLogoRenderer({ fill: cssVar(theme.text) })} />
        </div>
      </div>

      <div
        className="bg-background2 border-borderPrimary shadow-main pointer-events-auto flex flex-col flex-nowrap gap-5 border-b transition-transform"
        ref={ref}
      >
        <div className="mx-auto flex gap-9 pb-4">
          <MenuItems menuData={menuData} />
          <DefaultMenuMoreItems
            items={menuData.items}
            popOverMinAxis={-12}
            customMoreButton={
              <Flex
                className={
                  'text-text text-h6 group flex h-full cursor-pointer flex-col items-center justify-between leading-[26px]'
                }
              >
                <Plus size={'md'} />
                بیشتر
              </Flex>
            }
          />
        </div>
      </div>
    </div>
  );
};
